/**
 * ClaimOrgModal
 * A modal that lets any user submit a claim for an unclaimed organisation.
 * If the user is logged in, their name / email / phone are pre-filled (editable).
 */
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { submitOrgClaim } from '../api/api';

const ClaimOrgModal = ({ orgId, orgName, onClose }) => {
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState('');

  // Auto-fill from logged-in user
  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        phone: (user.phone || '').replace(/^\+91/, ''),
      });
    }
  }, [user]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Full name is required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'A valid email is required.';
    if (!form.phone.trim() || !/^\d{10}$/.test(form.phone)) errs.phone = 'Enter a valid 10-digit mobile number.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await submitOrgClaim(orgId, {
        requester_name: form.name.trim(),
        requester_email: form.email.trim().toLowerCase(),
        requester_phone: `+91${form.phone.trim()}`,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const modal = (
    <div
      className="fixed inset-0 z-[400] flex items-end sm:items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="claim-org-modal-backdrop"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        data-testid="claim-org-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">
              Claim Organisation Page
            </p>
            <h2
              className="text-lg font-bold text-gray-900 truncate"
              data-testid="claim-org-name"
            >
              {orgName}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="ml-3 flex-shrink-0 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Close"
            data-testid="claim-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-6">
          {success ? (
            /* ── Success state ── */
            <div className="text-center py-6" data-testid="claim-success-msg">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </div>
              <p className="text-gray-800 font-semibold text-base mb-2">Request Submitted!</p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Thanks. We&apos;ll review and get in touch within 24 hrs.
              </p>
              <button
                onClick={onClose}
                className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl transition-colors"
                data-testid="claim-success-close"
              >
                Done
              </button>
            </div>
          ) : (
            /* ── Form state ── */
            <form onSubmit={handleSubmit} noValidate>
              <p className="text-sm text-gray-500 mb-5">
                Fill in your details. We&rsquo;ll review and get in touch within 24 hrs.
              </p>

              {/* Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Your full name"
                  className={`input ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
                  autoComplete="name"
                  data-testid="claim-name-input"
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@example.com"
                  className={`input ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                  autoComplete="email"
                  data-testid="claim-email-input"
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <div className={`flex rounded-lg border overflow-hidden focus-within:ring-2 transition-all ${errors.phone ? 'border-red-400 focus-within:ring-red-400' : 'border-gray-300 focus-within:ring-green-500'}`}>
                  <span className="px-3 py-2.5 bg-gray-50 text-gray-500 border-r border-gray-300 text-sm font-medium flex-shrink-0 select-none">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setForm((f) => ({ ...f, phone: v }));
                      setErrors((er) => ({ ...er, phone: undefined }));
                    }}
                    placeholder="9876543210"
                    className="flex-1 px-3 py-2.5 focus:outline-none text-sm"
                    autoComplete="tel-national"
                    data-testid="claim-phone-input"
                  />
                </div>
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>

              {/* Server error */}
              {serverError && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4" data-testid="claim-server-error">
                  {serverError}
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-xl transition-colors"
                data-testid="claim-submit-btn"
              >
                {submitting ? 'Submitting\u2026' : 'Submit Claim'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ClaimOrgModal;
